import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, MessageCircle, Users, BarChart3, Settings, Copy,
  Check, Trash2, Send, Search, Code, ChevronRight, Mail,
  Phone as PhoneIcon, Globe, Clock, Loader2, X
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const ChatAgent = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };
  const msgEndRef = useRef(null);

  const [view, setView] = useState('conversations'); // conversations | detail | settings | embed
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewAvatar = 'https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=200&h=200&fit=crop&crop=face&q=80';
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [settings, setSettings] = useState({
    widget_enabled: true, greeting_message: 'Hi! How can I help you today?',
    widget_color: '#3b82f6', widget_position: 'right', agent_name: 'Mia',
    auto_qualify: true, collect_email: true, collect_phone: false,
    qualification_questions: [], away_message: "Thanks for reaching out! We'll get back to you soon."
  });
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [convRes, analyticsRes, settingsRes, embedRes] = await Promise.allSettled([
        axios.get(`${API}/chat/conversations`, { headers }),
        axios.get(`${API}/chat/analytics`, { headers }),
        axios.get(`${API}/chat/settings`, { headers }),
        axios.get(`${API}/chat/embed-code`, { headers }),
      ]);
      if (convRes.status === 'fulfilled') setConversations(convRes.value.data);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      if (settingsRes.status === 'fulfilled') setSettings(prev => ({ ...prev, ...settingsRes.value.data }));
      if (embedRes.status === 'fulfilled' && embedRes.value.data?.code) {
        setEmbedCode(embedRes.value.data.code);
      } else {
        const uid = user?.id;
        if (uid) setEmbedCode(`<script src="${BACKEND_URL}/api/chat/widget.js" data-miadesk-id="${uid}" async><\/script>`);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openConversation = async (conv) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${conv.id}`, { headers });
      setSelectedConv(res.data.conversation);
      setMessages(res.data.messages);
      setView('detail');
    } catch { toast({ title: 'Failed to load conversation', variant: 'destructive' }); }
  };

  const deleteConversation = async (id) => {
    try {
      await axios.delete(`${API}/chat/conversations/${id}`, { headers });
      setConversations(prev => prev.filter(c => c.id !== id));
      toast({ title: 'Conversation deleted' });
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/chat/settings`, settings, { headers });
      toast({ title: 'Chat settings saved' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSavingSettings(false); }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(embedCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const filteredConvs = conversations.filter(c =>
    !searchTerm || (c.visitor_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (c.visitor_email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { key: 'conversations', label: 'Conversations', icon: MessageCircle },
    { key: 'settings', label: 'Settings', icon: Settings },
    { key: 'embed', label: 'Embed Code', icon: Code },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="chat-agent-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {view === 'detail' && (
            <Button variant="ghost" onClick={() => setView('conversations')} className="text-gray-400 hover:text-white p-2" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold" data-testid="chat-agent-title">Chat Agent</h1>
            <p className="text-gray-500 text-sm">AI-powered live chat for your website</p>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="chat-analytics">
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold">{analytics.total_conversations || 0}</p>
            <p className="text-xs text-gray-500">Total Chats</p>
          </CardContent></Card>
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-400">{analytics.active_conversations || 0}</p>
            <p className="text-xs text-gray-500">Active</p>
          </CardContent></Card>
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-400">{analytics.total_messages || 0}</p>
            <p className="text-xs text-gray-500">Messages</p>
          </CardContent></Card>
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-400">{analytics.leads_captured || 0}</p>
            <p className="text-xs text-gray-500">Leads Captured</p>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/[0.03] rounded-lg p-1 w-fit" data-testid="chat-tabs">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setView(tab.key)} data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${view === tab.key || (view === 'detail' && tab.key === 'conversations') ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ CONVERSATIONS LIST ═══ */}
        {view === 'conversations' && (
          <div data-testid="conversations-view">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search conversations..." className="pl-10 bg-white/[0.03] border-white/[0.06] text-white" data-testid="search-conversations" />
              </div>
            </div>

            {loading ? <p className="text-gray-500 text-sm py-8 text-center">Loading...</p> :
              filteredConvs.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="py-16 text-center">
                  <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-400 mb-1">No conversations yet</h3>
                  <p className="text-sm text-gray-500 mb-4">Embed the chat widget on your website to start receiving messages.</p>
                  <Button onClick={() => setView('embed')} className="bg-blue-600 hover:bg-blue-700 text-white"><Code className="w-4 h-4 mr-2" /> Get Embed Code</Button>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {filteredConvs.map(conv => (
                    <Card key={conv.id} className="bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12] transition-colors cursor-pointer" data-testid={`conv-${conv.id}`}>
                      <CardContent className="p-4 flex items-center gap-4" onClick={() => openConversation(conv)}>
                        <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-sm font-bold flex-shrink-0">
                          {(conv.visitor_name || 'V').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white truncate">{conv.visitor_name || 'Visitor'}</p>
                            {conv.visitor_email && <span className="text-[10px] text-gray-500 truncate">{conv.visitor_email}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {conv.page_url && <span className="text-[10px] text-gray-600 truncate flex items-center gap-1"><Globe className="w-3 h-3" />{new URL(conv.page_url).pathname}</span>}
                            <span className="text-[10px] text-gray-600 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(conv.updated_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-500">{conv.message_count || 0} msgs</span>
                          {conv.utm_source && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">{conv.utm_source}</span>}
                          <button onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }} className="text-gray-600 hover:text-red-400 transition-colors p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* ═══ CONVERSATION DETAIL ═══ */}
        {view === 'detail' && selectedConv && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="conversation-detail">
            {/* Messages */}
            <div className="lg:col-span-2">
              <Card className="bg-white/[0.03] border-white/[0.06]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm">Chat with {selectedConv.visitor_name || 'Visitor'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px] overflow-y-auto space-y-3 pr-2 mb-4" data-testid="messages-container">
                    {messages.map((msg, i) => (
                      <div key={msg.id || i} className={`flex ${msg.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                          msg.role === 'visitor' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white/[0.06] text-gray-200 rounded-bl-sm'
                        }`}>
                          {msg.content}
                          <p className="text-[10px] mt-1 opacity-50">{new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={msgEndRef} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Visitor Info */}
            <Card className="bg-white/[0.03] border-white/[0.06]" data-testid="visitor-info">
              <CardHeader><CardTitle className="text-white text-sm">Visitor Info</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {selectedConv.visitor_name && <div className="flex items-center gap-2 text-sm"><Users className="w-4 h-4 text-gray-500" /><span className="text-gray-300">{selectedConv.visitor_name}</span></div>}
                  {selectedConv.visitor_email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-500" /><span className="text-gray-300">{selectedConv.visitor_email}</span></div>}
                  {selectedConv.visitor_phone && <div className="flex items-center gap-2 text-sm"><PhoneIcon className="w-4 h-4 text-gray-500" /><span className="text-gray-300">{selectedConv.visitor_phone}</span></div>}
                  {selectedConv.page_url && <div className="flex items-center gap-2 text-sm"><Globe className="w-4 h-4 text-gray-500" /><span className="text-gray-300 text-xs truncate">{selectedConv.page_url}</span></div>}
                </div>

                {/* Marketing Data */}
                {(selectedConv.utm_source || selectedConv.utm_medium || selectedConv.utm_campaign || selectedConv.referrer) && (
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Marketing Data</p>
                    <div className="space-y-2 text-xs">
                      {selectedConv.utm_source && <div className="flex justify-between"><span className="text-gray-500">Source</span><span className="text-purple-400">{selectedConv.utm_source}</span></div>}
                      {selectedConv.utm_medium && <div className="flex justify-between"><span className="text-gray-500">Medium</span><span className="text-purple-400">{selectedConv.utm_medium}</span></div>}
                      {selectedConv.utm_campaign && <div className="flex justify-between"><span className="text-gray-500">Campaign</span><span className="text-purple-400">{selectedConv.utm_campaign}</span></div>}
                      {selectedConv.referrer && <div className="flex justify-between"><span className="text-gray-500">Referrer</span><span className="text-gray-400 truncate ml-2">{selectedConv.referrer}</span></div>}
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-white/5">
                  <p className="text-xs text-gray-500">Started: {new Date(selectedConv.created_at).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Messages: {selectedConv.message_count || messages.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ SETTINGS ═══ */}
        {view === 'settings' && (
          <div className="max-w-2xl" data-testid="chat-settings">
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardHeader>
                <CardTitle className="text-white">Widget Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">Enable Widget</Label><p className="text-gray-500 text-xs">Show chat widget on your website</p></div>
                  <Switch checked={settings.widget_enabled} onCheckedChange={v => setSettings(p => ({ ...p, widget_enabled: v }))} data-testid="widget-toggle" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 text-sm">Agent Name</Label>
                    <Input value={settings.agent_name} onChange={e => setSettings(p => ({ ...p, agent_name: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="agent-name" />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-sm">Widget Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={settings.widget_color} onChange={e => setSettings(p => ({ ...p, widget_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border-0" data-testid="widget-color" />
                      <Input value={settings.widget_color} onChange={e => setSettings(p => ({ ...p, widget_color: e.target.value }))} className="bg-black/30 border-white/10 text-white flex-1" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Greeting Message</Label>
                  <Input value={settings.greeting_message} onChange={e => setSettings(p => ({ ...p, greeting_message: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="greeting-message" />
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Away Message</Label>
                  <Input value={settings.away_message} onChange={e => setSettings(p => ({ ...p, away_message: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="away-message" />
                </div>

                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">Collect Email</Label><p className="text-gray-500 text-xs">AI will naturally ask for visitor's email</p></div>
                  <Switch checked={settings.collect_email} onCheckedChange={v => setSettings(p => ({ ...p, collect_email: v }))} data-testid="collect-email" />
                </div>

                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">Collect Phone</Label><p className="text-gray-500 text-xs">AI will ask for phone number</p></div>
                  <Switch checked={settings.collect_phone} onCheckedChange={v => setSettings(p => ({ ...p, collect_phone: v }))} data-testid="collect-phone" />
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Widget Position</Label>
                  <div className="flex gap-2 mt-1">
                    {['left', 'right'].map(pos => (
                      <button key={pos} onClick={() => setSettings(p => ({ ...p, widget_position: pos }))}
                        className={`px-4 py-2 rounded-lg text-sm capitalize transition-all ${settings.widget_position === pos ? 'bg-blue-600 text-white' : 'bg-white/[0.03] text-gray-400 border border-white/[0.06]'}`}
                        data-testid={`position-${pos}`}>
                        {pos}
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={saveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="save-settings-btn">
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ EMBED CODE ═══ */}
        {view === 'embed' && (
          <div className="max-w-3xl" data-testid="embed-view">
            {/* Step 1: Copy Code */}
            <Card className="bg-white/[0.03] border-white/[0.06] mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">1</div>
                  <div>
                    <CardTitle className="text-white text-base">Copy your embed code</CardTitle>
                    <p className="text-gray-500 text-xs mt-0.5">This one line of code adds the AI chat widget to your website</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <div className="flex justify-end mb-2">
                    <Button onClick={copyCode} className={`h-9 px-4 rounded-lg text-sm font-medium transition-all ${copied ? 'bg-emerald-600 text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-testid="copy-embed-btn">
                      {copied ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Code</>}
                    </Button>
                  </div>
                  <pre className="bg-black/60 border border-white/10 rounded-xl p-4 text-sm text-emerald-400 font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-code-display">
                    {embedCode}
                  </pre>
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Paste on Website */}
            <Card className="bg-white/[0.03] border-white/[0.06] mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">2</div>
                  <div>
                    <CardTitle className="text-white text-base">Paste it on your website</CardTitle>
                    <p className="text-gray-500 text-xs mt-0.5">Choose your website platform for specific instructions</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Platform tabs */}
                  {[
                    {
                      name: 'WordPress',
                      icon: '🌐',
                      steps: [
                        'Go to your WordPress Admin → Appearance → Theme File Editor',
                        'Select "footer.php" from the right sidebar',
                        'Paste the code just before the </body> tag',
                        'Click "Update File" to save',
                      ],
                      alt: 'Or install a plugin like "Insert Headers and Footers" → paste code in the Footer Scripts box'
                    },
                    {
                      name: 'Shopify',
                      icon: '🛍',
                      steps: [
                        'Go to Online Store → Themes → Actions → Edit Code',
                        'Open "theme.liquid" file',
                        'Paste the code just before the </body> tag',
                        'Click Save',
                      ],
                    },
                    {
                      name: 'Wix',
                      icon: '✨',
                      steps: [
                        'Go to Settings → Custom Code (under Advanced)',
                        'Click "+ Add Custom Code"',
                        'Paste the code, name it "Miadesk Chat"',
                        'Set placement to "Body - end" and apply to "All pages"',
                      ],
                    },
                    {
                      name: 'Squarespace',
                      icon: '◼',
                      steps: [
                        'Go to Settings → Advanced → Code Injection',
                        'Paste the code in the "Footer" section',
                        'Click Save',
                      ],
                    },
                    {
                      name: 'Hostinger',
                      icon: '🚀',
                      steps: [
                        'Log in to hPanel and open Website Builder → Edit Website',
                        'Go to Settings → Integrations → Custom code',
                        'Paste the code and set location to "Body - end"',
                        'Click Save, then Update Website to publish',
                      ],
                      alt: 'Using WordPress on Hostinger? Follow the WordPress steps above instead (Appearance → Theme File Editor → footer.php)'
                    },
                    {
                      name: 'HTML',
                      icon: '</>',
                      steps: [
                        'Open your website\'s HTML file(s)',
                        'Find the </body> closing tag (near the bottom)',
                        'Paste the code on the line just above </body>',
                        'Save and upload the file to your server',
                      ],
                    },
                  ].map((platform) => (
                    <details key={platform.name} className="group rounded-xl border border-white/[0.06] overflow-hidden" data-testid={`platform-${platform.name.toLowerCase()}`}>
                      <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.03] transition-colors list-none">
                        <span className="text-lg w-6 text-center">{platform.icon}</span>
                        <span className="text-sm font-medium text-white flex-1">{platform.name}</span>
                        <ChevronRight className="w-4 h-4 text-gray-500 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="px-4 pb-4 pt-1 border-t border-white/5">
                        <ol className="space-y-2 ml-1">
                          {platform.steps.map((step, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm">
                              <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                              <span className="text-gray-300 leading-relaxed">{step}</span>
                            </li>
                          ))}
                        </ol>
                        {platform.alt && (
                          <div className="mt-3 p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/15">
                            <p className="text-xs text-purple-300 leading-relaxed"><span className="font-semibold">Easier option:</span> {platform.alt}</p>
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Verify */}
            <Card className="bg-white/[0.03] border-white/[0.06] mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">3</div>
                  <div>
                    <CardTitle className="text-white text-base">Test your widget</CardTitle>
                    <p className="text-gray-500 text-xs mt-0.5">Make sure everything is working</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
                    <p className="text-sm font-medium text-emerald-300 mb-2">After adding the code:</p>
                    <ul className="text-xs text-gray-400 space-y-1.5">
                      <li className="flex items-start gap-2"><Check className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />Visit your website — you should see a chat bubble in the bottom corner</li>
                      <li className="flex items-start gap-2"><Check className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />Click the bubble and send a test message</li>
                      <li className="flex items-start gap-2"><Check className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />Check the "Conversations" tab here — your test message should appear</li>
                    </ul>
                  </div>
                  <div className="flex-1 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <p className="text-sm font-medium text-white mb-1">Live Preview</p>
                    <p className="text-xs text-gray-500 mb-3">This is exactly how the widget looks on your site — click the bubble to try it</p>
                    <div className="relative h-80 rounded-lg overflow-hidden border border-white/[0.06]" style={{ background: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)' }} data-testid="widget-preview-frame">
                      {/* mock page content */}
                      <div className="p-5 space-y-2.5" style={{ opacity: 0.5 }}>
                        <div className="h-3.5 w-1/2 rounded" style={{ background: '#cbd5e1' }} />
                        <div className="h-2.5 w-3/4 rounded" style={{ background: '#cbd5e1' }} />
                        <div className="h-2.5 w-2/3 rounded" style={{ background: '#cbd5e1' }} />
                        <div className="h-2.5 w-1/3 rounded" style={{ background: '#cbd5e1' }} />
                      </div>
                      {/* chat panel */}
                      {previewOpen && (
                        <div className={`absolute bottom-[72px] ${settings.widget_position === 'left' ? 'left-3' : 'right-3'} w-64 rounded-xl overflow-hidden`} style={{ boxShadow: '0 20px 50px rgba(0,0,0,0.3)', background: '#ffffff' }} data-testid="widget-preview-panel">
                          <div className="px-3 py-2.5 flex items-center gap-2" style={{ background: settings.widget_color }}>
                            <img src={settings.agent_avatar || previewAvatar} alt="Agent" className="w-7 h-7 rounded-full object-cover flex-shrink-0" style={{ border: '2px solid rgba(255,255,255,0.4)' }} data-testid="preview-agent-avatar" />
                            <span className="text-sm font-medium" style={{ color: '#ffffff' }}>{settings.agent_name || 'Mia'}</span>
                            <span className="w-2 h-2 rounded-full inline-block ml-auto" style={{ background: '#6ee7b7' }} />
                          </div>
                          <div className="p-3 h-36" style={{ background: '#f8fafc' }}>
                            <div className="max-w-[85%] rounded-lg rounded-tl-none px-3 py-2 text-xs leading-relaxed" style={{ background: '#ffffff', color: '#334155', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                              {settings.greeting_message || 'Hi! How can I help you today?'}
                            </div>
                          </div>
                          <div className="px-3 py-2 flex items-center gap-2" style={{ background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                            <div className="flex-1 text-[11px] rounded-full px-3 py-1.5" style={{ background: '#f1f5f9', color: '#94a3b8' }}>Type a message...</div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: settings.widget_color }}>
                              <Send className="w-3 h-3" style={{ color: '#ffffff' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      {/* bubble */}
                      <button
                        onClick={() => setPreviewOpen(o => !o)}
                        className={`absolute bottom-3 ${settings.widget_position === 'left' ? 'left-3' : 'right-3'} w-12 h-12 rounded-full flex items-center justify-center hover:scale-105 transition-transform overflow-hidden`}
                        style={{ background: settings.widget_color, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}
                        data-testid="widget-preview-bubble"
                      >
                        {previewOpen
                          ? <X className="w-5 h-5" style={{ color: '#ffffff' }} />
                          : <img src={settings.agent_avatar || previewAvatar} alt="Agent" className="w-full h-full object-cover rounded-full" data-testid="preview-bubble-avatar" />}
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Customize the color, position, greeting and agent name in the <button className="text-blue-400 hover:text-blue-300 underline" onClick={() => setView('settings')} data-testid="preview-goto-settings">Settings tab</button>.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Troubleshooting */}
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Settings className="w-4 h-4 text-gray-400" /> Troubleshooting
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { q: "I don't see the chat bubble", a: 'Make sure the code is pasted before </body>, not inside <head>. Clear your browser cache and reload.' },
                    { q: 'The widget looks different than expected', a: 'Go to the Settings tab to customize the widget color, position, greeting message, and agent name.' },
                    { q: 'Messages are not showing in my dashboard', a: 'Send a test message through the widget. It may take a few seconds to appear in the Conversations tab.' },
                    { q: 'Can I use this on multiple websites?', a: 'Yes! The same code works on any website. All conversations will appear in one dashboard.' },
                  ].map((faq) => (
                    <details key={faq.q} className="group">
                      <summary className="text-sm text-gray-300 cursor-pointer hover:text-white transition-colors list-none flex items-center gap-2">
                        <ChevronRight className="w-3.5 h-3.5 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0" />
                        {faq.q}
                      </summary>
                      <p className="text-xs text-gray-500 mt-1.5 ml-5 leading-relaxed">{faq.a}</p>
                    </details>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatAgent;
